import React, { useEffect } from "react";
import styled from "styled-components";

import { Nav } from "../components/Nav";
import Footer from "../components/Footer";
import { Icon } from "../components/Icon";
import { PROJECT_CASES } from "../data/projects";
import { waLink } from "../data/diagnostico";
import {
    SITE_URL,
    setMetaTag,
    setLinkTag,
    setStructuredData,
    removeStructuredData,
    restoreDefaultMeta
} from "../utils/pageMeta";

const CASES_URL = `${SITE_URL}/cases`;
const CASES_TITLE = "Cases · Mango Software House";
const CASES_DESCRIPTION = "Quatro empresas, quatro gargalos diferentes: o que estava travando, o que foi entregue e o resultado, com métrica e depoimento reais.";

const Page = styled.main`
    width: 100%;
    padding: 148px 0 0;
    overflow-x: hidden;

    @media only screen and (max-width: 720px) {
        padding-top: 118px;
    }
`;

const Section = styled.section`
    width: min(900px, calc(100vw - 32px));
    margin: 0 auto;

    @media only screen and (max-width: 640px) {
        width: min(100%, calc(100vw - 24px));
    }
`;

const Hero = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 40px;
`;

const Eyebrow = styled.span`
    font-size: 12.5px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--brand);
`;

const Title = styled.h1`
    margin: 0;
    font-size: clamp(2.1rem, 4.4vw, 3rem);
    font-weight: 800;
    line-height: 1.06;
    letter-spacing: -0.03em;
    color: var(--ink);
    max-width: 22ch;
    text-wrap: balance;
`;

const Lead = styled.p`
    margin: 0;
    max-width: 60ch;
    font-size: 1.15rem;
    font-weight: 500;
    line-height: 1.6;
    color: var(--muted);
`;

const SetorChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const SetorChip = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    padding: 0 13px;
    border-radius: 12px;
    background: #f2ecfb;
    color: var(--brand-2);
    font-size: 13px;
    font-weight: 700;
`;

const CasesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-bottom: 44px;
`;

const CaseArticle = styled.article`
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding: 34px;
    border-radius: 22px;
    background: #fff;
    border: 1px solid var(--line);
    box-shadow: 0 1px 2px rgba(26, 19, 48, 0.05), 0 14px 40px rgba(26, 19, 48, 0.05);

    @media only screen and (max-width: 640px) {
        padding: 22px;
        border-radius: 18px;
    }
`;

const CaseHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
`;

const CaseClientRow = styled.div`
    display: flex;
    align-items: center;
    gap: 18px;
`;

const CaseLogo = styled.img`
    height: 52px;
    width: auto;
    max-width: 140px;
    object-fit: contain;
`;

const CaseClientMeta = styled.span`
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const CaseClientName = styled.strong`
    font-size: 19px;
    font-weight: 800;
    letter-spacing: -0.015em;
    color: var(--ink);
`;

const CaseClientSub = styled.span`
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--muted);

    .material-symbols-rounded { font-size: 15px; color: var(--muted-soft); }
`;

const CaseTag = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    padding: 0 13px;
    border-radius: 12px;
    background: #f2ecfb;
    color: var(--brand-2);
    font-size: 12.5px;
    font-weight: 800;
`;

const CaseHeadline = styled.h2`
    margin: 0;
    font-size: clamp(1.4rem, 2.6vw, 1.8rem);
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--ink);
    max-width: 32ch;
`;

const CaseTwoCol = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 22px;

    @media only screen and (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const ColLabel = styled.span`
    display: block;
    margin-bottom: 9px;
    font-size: 11.5px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${({ $tone }) => ($tone === "success" ? "var(--success-strong)" : "var(--warning)")};
`;

const ProblemText = styled.p`
    margin: 0;
    font-size: 15.5px;
    font-weight: 500;
    line-height: 1.6;
    color: var(--muted);
`;

const DeliveryRow = styled.span`
    display: flex;
    align-items: flex-start;
    gap: 9px;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
    color: var(--ink);

    .material-symbols-rounded { color: var(--success); margin-top: 2px; }
`;

const MetricBlock = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
    gap: 22px;
    align-items: center;
    padding: 24px;
    border-radius: 18px;
    background: var(--ink);
    color: #fff;

    @media only screen and (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 14px;
    }
`;

const MetricValue = styled.strong`
    display: block;
    font-size: 38px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.04em;
    color: var(--brand-3);
`;

const MetricLabel = styled.span`
    display: block;
    margin-top: 4px;
    font-size: 14.5px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.86);
`;

const MetricQuote = styled.p`
    margin: 0;
    font-family: var(--font-quote);
    font-style: italic;
    font-size: 18px;
    line-height: 1.5;
    color: #fff;
`;

const MetricAuthor = styled.span`
    display: block;
    margin-top: 8px;
    font-size: 13px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.72);
`;

const CaseActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
`;

const RouteButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 9px;
    min-height: 50px;
    padding: 0 20px;
    border-radius: 12px;
    background: #f2ecfb;
    color: var(--brand-2);
    font-size: 15px;
    font-weight: 800;

    &:hover { background: #e6dcfa; }
`;

const WaButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 9px;
    min-height: 50px;
    padding: 0 18px;
    border-radius: 12px;
    border: 1.5px solid #d9d0ee;
    color: var(--ink);
    font-size: 15px;
    font-weight: 700;

    .material-symbols-rounded { color: var(--success); }
    &:hover { border-color: var(--brand); }
`;

const ClosingSection = styled.div`
    margin-top: 12px;
    background: var(--ink);
`;

const ClosingInner = styled.div`
    width: min(900px, calc(100vw - 32px));
    margin: 0 auto;
    padding: 52px 0;
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    gap: 44px;
    align-items: center;

    @media only screen and (max-width: 720px) {
        grid-template-columns: 1fr;
        gap: 24px;
    }
`;

const ClosingTitle = styled.h2`
    margin: 0 0 16px;
    font-size: clamp(1.6rem, 3vw, 2.1rem);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.03em;
    color: #fff;
    max-width: 24ch;
`;

const ClosingText = styled.p`
    margin: 0;
    max-width: 48ch;
    font-size: 16.5px;
    font-weight: 500;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.78);
`;

const ClosingActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ClosingPrimary = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 56px;
    padding: 0 22px;
    border-radius: 999px;
    background: var(--brand);
    color: #fff;
    font-size: 16.5px;
    font-weight: 800;

    &:hover { background: var(--brand-strong); }
`;

const ClosingSecondary = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    min-height: 56px;
    padding: 0 22px;
    border-radius: 12px;
    border: 1.5px solid rgba(255, 255, 255, 0.24);
    color: #fff;
    font-size: 16.5px;
    font-weight: 700;

    .material-symbols-rounded { color: var(--brand-3); }
`;

function Projects() {
    useEffect(() => {
        document.title = CASES_TITLE;
        setMetaTag('meta[name="description"]', { name: "description", content: CASES_DESCRIPTION });
        setMetaTag('meta[property="og:title"]', { property: "og:title", content: CASES_TITLE });
        setMetaTag('meta[property="og:description"]', { property: "og:description", content: CASES_DESCRIPTION });
        setMetaTag('meta[property="og:url"]', { property: "og:url", content: CASES_URL });
        setLinkTag('link[rel="canonical"]', { rel: "canonical", href: CASES_URL });
        setStructuredData("cases-schema", {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Início", item: SITE_URL },
                        { "@type": "ListItem", position: 2, name: "Cases", item: CASES_URL }
                    ]
                },
                {
                    "@type": "CollectionPage",
                    name: "Cases da Mango Software House",
                    description: CASES_DESCRIPTION,
                    hasPart: PROJECT_CASES.map((item) => ({
                        "@type": "Article",
                        headline: item.headline,
                        about: item.setor,
                        contentLocation: { "@type": "Place", name: item.local },
                        articleSection: item.category,
                        description: item.problema,
                        author: { "@type": "Organization", name: "Mango Software House" }
                    }))
                }
            ]
        });

        return () => {
            removeStructuredData("cases-schema");
            restoreDefaultMeta();
        };
    }, []);

    const waGeral = waLink("Olá! Vim pela página de cases do site e quero conversar sobre o meu caso.");

    return (
        <>
            <Nav />

            <Page id="top">
                <Section>
                    <Hero>
                        <Eyebrow>Cases</Eyebrow>
                        <Title>Quatro empresas, quatro gargalos diferentes</Title>
                        <Lead>Cuiabá, Natal e Porto. Seguros, colecionáveis, detalhe automotivo e consultoria gastronômica. Setores sem nada em comum — e um mesmo jeito de trabalhar: entender o que trava antes de propor solução.</Lead>
                        <SetorChips>
                            {PROJECT_CASES.map((item) => <SetorChip key={item.setor}>{item.setor}</SetorChip>)}
                        </SetorChips>
                    </Hero>

                    <CasesList>
                        {PROJECT_CASES.map((item) => (
                            <CaseArticle key={item.name}>
                                <CaseHead>
                                    <CaseClientRow>
                                        <CaseLogo src={item.logo} alt={item.logoAlt} />
                                        <CaseClientMeta>
                                            <CaseClientName>{item.name}</CaseClientName>
                                            <CaseClientSub>
                                                {item.setor}
                                                <Icon name="place" size="15px" />
                                                {item.local}
                                            </CaseClientSub>
                                        </CaseClientMeta>
                                    </CaseClientRow>
                                    <CaseTag><Icon name={item.icon} size="17px" />{item.category}</CaseTag>
                                </CaseHead>

                                <CaseHeadline>{item.headline}</CaseHeadline>

                                <CaseTwoCol>
                                    <div>
                                        <ColLabel>O que estava travando</ColLabel>
                                        <ProblemText>{item.problema}</ProblemText>
                                    </div>
                                    <div>
                                        <ColLabel $tone="success">O que foi entregue</ColLabel>
                                        <div style={{ display: "grid", gap: "6px" }}>
                                            {item.deliverables.map((entrega) => (
                                                <DeliveryRow key={entrega}>
                                                    <Icon name="check_circle" size="18px" />
                                                    {entrega}
                                                </DeliveryRow>
                                            ))}
                                        </div>
                                    </div>
                                </CaseTwoCol>

                                <MetricBlock>
                                    <div>
                                        <MetricValue>{item.metric}</MetricValue>
                                        <MetricLabel>{item.metricLabel}</MetricLabel>
                                    </div>
                                    <div>
                                        <MetricQuote>“{item.quote}”</MetricQuote>
                                        <MetricAuthor>{item.author}</MetricAuthor>
                                    </div>
                                </MetricBlock>

                                <CaseActions>
                                    <RouteButton href={`/diagnostico/${item.rotaSlug}`}>
                                        Ver como funciona esse caminho
                                        <Icon name="arrow_forward" size="19px" />
                                    </RouteButton>
                                    <WaButton href={waLink(`Olá! Vi o case da ${item.name} no site e tenho um caso parecido.`)} target="_blank" rel="noreferrer">
                                        <Icon name="chat" size="19px" />
                                        Tenho um caso parecido
                                    </WaButton>
                                </CaseActions>
                            </CaseArticle>
                        ))}
                    </CasesList>
                </Section>

                <ClosingSection>
                    <ClosingInner>
                        <div>
                            <ClosingTitle>Seu caso não precisa parecer com nenhum destes</ClosingTitle>
                            <ClosingText>Comece pela situação que mais se aproxima — ou pela opção de descrever do seu jeito. A primeira conversa é sem custo e a resposta sai em até 1 dia útil.</ClosingText>
                        </div>
                        <ClosingActions>
                            <ClosingPrimary href="/#diagnostico">
                                Escolher a minha situação
                                <Icon name="arrow_forward" size="20px" />
                            </ClosingPrimary>
                            <ClosingSecondary href={waGeral} target="_blank" rel="noreferrer">
                                <Icon name="chat" size="20px" />
                                Falar no WhatsApp
                            </ClosingSecondary>
                        </ClosingActions>
                    </ClosingInner>
                </ClosingSection>
            </Page>

            <Footer />
        </>
    );
}

export { Projects };
export default Projects;
